import { useSearchParams } from 'react-router-dom';
import {
  EVERPRO_DOMAIN,
  getAppDomain,
  JOIST_DOMAIN,
  SERVICE_NATION_BY_EVER_PRO_DOMAIN,
  UNKNOWN_DOMAIN,
} from './domains';
import {
  EverProEdgePublicSolutionOrgName,
  ImproveIt360SolutionOrgName,
  InvoiceSimpleSolutionOrgName,
  JoistCode,
  JoistSolutionOrgName,
  KickservSolutionOrgName,
  MarketSharpSolutionOrgName,
  RoofsnapSolutionOrgName,
  ServiceFusionSolutionOrgName,
  ServiceNationSolutionOrgName,
} from '../common/constants';
import { selectMemberSession } from '../store/slices/onboardingSlice';
import { useAppSelector } from '../store/hooks';
import { WALLET_ROUTE } from '../routes/namedRoutes';

interface SolutionOrgs {
  [key: string]: {
    [key: string]: unknown;
    isNativeAppExperience?: boolean;
    isNativeAppLoginExperience?: boolean;
  }
}

type NativeAppWindow = Window & {
  webkit?: {
    messageHandlers?: {
      optimusEvent?: {
        postMessage: (message: string) => void;
      };
    };
  };
  android?: {
    postMessage: (message: string) => void;
  };
  ReactNativeWebView?: {
    postMessage: (message: string) => void;
  };
};

const isIosWebView = () => {
  const { userAgent } = window.navigator;
  const isIos = /iPad|iPhone|iPod/.test(userAgent);
  const isWebKit = /WebKit/.test(userAgent);
  const isSafari = /Safari/.test(userAgent);
  const isChromeIos = /CriOS/.test(userAgent);
  const isFirefoxIos = /FxiOS/.test(userAgent);

  return isIos && isWebKit && !isSafari && !isChromeIos && !isFirefoxIos;
};

const getIsNativeAppExperience = () => window !== window.parent;

const getIsNativeAppLoginExperience = () => {
  const nativeWindow = window as NativeAppWindow;

  return getIsNativeAppExperience() ||
    !!nativeWindow.webkit?.messageHandlers?.optimusEvent?.postMessage ||
    !!nativeWindow.android?.postMessage ||
    !!nativeWindow.ReactNativeWebView?.postMessage ||
    isIosWebView();
};

// manage solution org related resources based on the current domain
export function useSolutionOrg() {
  const { solutionOrgId } = useAppSelector(selectMemberSession); // obtained after login
  const appDomain = getAppDomain();

  const [searchParams] = useSearchParams();
  const isImproveIt360 = searchParams.get('isImproveIt360') === 'true';
  const computedSolutionOrgId =
    solutionOrgId || (isImproveIt360 ? process.env.REACT_APP_IMPROVE_IT_360_SOLUTION_ID : '');
  const isNativeAppExperience = getIsNativeAppExperience();
  const isNativeAppLoginExperience = getIsNativeAppLoginExperience();

  const getEverproResources = () => {
    if (![EVERPRO_DOMAIN].includes(appDomain)) {
      return {};
    }
    const everProResources: {
      [key: string]: unknown;
      isNativeAppExperience?: boolean;
      isNativeAppLoginExperience?: boolean;
    } = {
      envSolutionOrgTitle: process.env.REACT_APP_EVER_PRO_SITE_TITLE,
      termsOfServiceUrl: 'https://servicenation.com/edge-tos/',
      privacyPolicyUrl: 'https://servicenation.com/privacy-policy/',
      numberOfOnboardingForms: 4,
      // Everpro Edge will also use the Joist code when enrolling in Home Depot
      programCode: JoistCode,
      solutionOrgSpecificAppName: 'EverPro Edge',
      // TODO: update these flags
      solutionOrgHasOnlyHomeDepot: true,
      hasDateBasedPartnerListCard: true,
      enrollOnSplashScreen: false,
      paypalConnectionReturnUrl: `${process.env.REACT_APP_EVERPRO_BASE_URL}${WALLET_ROUTE}`,
      domainName: appDomain,
      productQuestion: true,
      baseUrl: `${process.env.REACT_APP_EVERPRO_BASE_URL}`,
      isNativeAppLoginExperience,
    };

    // add everpro solution org specific resources
    switch (computedSolutionOrgId) {
      case process.env.REACT_APP_KICKSERV_SOLUTION_ID:
        everProResources.solutionOrgName = KickservSolutionOrgName;
        everProResources.envSolutionOrgFormId = process.env.REACT_APP_KICKSERV_FORM_ID;
        everProResources.envSolutionOrgId = process.env.REACT_APP_KICKSERV_SOLUTION_ID;
        break;
      case process.env.REACT_APP_ROOFSNAP_SOLUTION_ID:
        everProResources.solutionOrgName = RoofsnapSolutionOrgName;
        everProResources.envSolutionOrgFormId = process.env.REACT_APP_ROOFSNAP_FORM_ID;
        everProResources.envSolutionOrgId = process.env.REACT_APP_ROOFSNAP_SOLUTION_ID;
        break;
      case process.env.REACT_APP_MARKETSHARP_SOLUTION_ID:
        everProResources.solutionOrgName = MarketSharpSolutionOrgName;
        everProResources.envSolutionOrgFormId = process.env.REACT_APP_MARKETSHARP_FORM_ID;
        everProResources.envSolutionOrgId = process.env.REACT_APP_MARKETSHARP_SOLUTION_ID;
        break;
      case process.env.REACT_APP_IMPROVE_IT_360_SOLUTION_ID:
        everProResources.solutionOrgName = ImproveIt360SolutionOrgName;
        everProResources.envSolutionOrgFormId = process.env.REACT_APP_IMPROVE_IT_360_FORM_ID;
        everProResources.envSolutionOrgId = process.env.REACT_APP_IMPROVE_IT_360_SOLUTION_ID;
        break;
      case process.env.REACT_APP_SERVICE_NATION_SOLUTION_ID:
        everProResources.solutionOrgName = ServiceNationSolutionOrgName;
        everProResources.envSolutionOrgFormId = process.env.REACT_APP_SERVICE_NATION_FORM_ID;
        everProResources.envSolutionOrgId = process.env.REACT_APP_SERVICE_NATION_SOLUTION_ID;
        break;
      default: // invoice simple is the default case
        everProResources.solutionOrgName = InvoiceSimpleSolutionOrgName;
        everProResources.envSolutionOrgFormId = process.env.REACT_APP_INVOICE_SIMPLE_FORM_ID;
        everProResources.envSolutionOrgId = process.env.REACT_APP_INVOICE_SIMPLE_SOLUTION_ID;
        everProResources.isNativeAppExperience = isNativeAppExperience;
        break;
    }

    return everProResources;
  };

  const getSNByEverproResources = () => {
    if (![SERVICE_NATION_BY_EVER_PRO_DOMAIN].includes(appDomain)) {
      return {};
    }
    const snResources: {
      [key: string]: unknown;
      isNativeAppExperience?: boolean;
      isNativeAppLoginExperience?: boolean;
    } = {
      envSolutionOrgTitle: process.env.REACT_APP_SERVICE_NATION_BY_EVERPRO_SITE_TITLE,
      termsOfServiceUrl: 'https://servicenation.com/edge-tos/',
      privacyPolicyUrl: 'https://servicenation.com/privacy-policy/',
      numberOfOnboardingForms: 4,
      // Everpro Edge will also use the Joist code when enrolling in Home Depot
      programCode: JoistCode,
      solutionOrgSpecificAppName: 'Service Nation',
      // TODO: update these flags
      solutionOrgHasOnlyHomeDepot: true,
      hasDateBasedPartnerListCard: true,
      enrollOnSplashScreen: false,
      paypalConnectionReturnUrl: `${process.env.REACT_APP_SERVICE_NATION_BY_EVER_PRO_BASE_URL}${WALLET_ROUTE}`,
      domainName: appDomain,
      productQuestion: false,
      baseUrl: `${process.env.REACT_APP_SERVICE_NATION_BY_EVER_PRO_BASE_URL}`,
      isNativeAppLoginExperience,
    };

    // add SN solution org specific resources
    switch (computedSolutionOrgId) {
      case process.env.REACT_APP_EVERPRO_EDGE_PUBLIC_SOLUTION_ID:
        snResources.solutionOrgName = EverProEdgePublicSolutionOrgName;
        snResources.envSolutionOrgId = process.env.REACT_APP_EVERPRO_EDGE_PUBLIC_SOLUTION_ID;
        snResources.envSolutionOrgFormId = process.env.REACT_APP_EVERPRO_EDGE_PUBLIC_FORM_ID;
        break;
      case process.env.REACT_APP_SERVICE_NATION_SOLUTION_ID:
        snResources.solutionOrgName = ServiceNationSolutionOrgName;
        snResources.envSolutionOrgId = process.env.REACT_APP_SERVICE_NATION_SOLUTION_ID;
        snResources.envSolutionOrgFormId = process.env.REACT_APP_SERVICE_NATION_FORM_ID;
        break;
      case process.env.REACT_APP_SF_SOLUTION_ID:
        snResources.solutionOrgName = ServiceFusionSolutionOrgName;
        snResources.envSolutionOrgId = process.env.REACT_APP_SF_SOLUTION_ID;
        snResources.envSolutionOrgFormId = process.env.REACT_APP_SF_FORM_ID;
        break;
      default: // Edge Public is the default case
        snResources.solutionOrgName = EverProEdgePublicSolutionOrgName;
        snResources.envSolutionOrgId = process.env.REACT_APP_EVERPRO_EDGE_PUBLIC_SOLUTION_ID;
        snResources.envSolutionOrgFormId = process.env.REACT_APP_EVERPRO_EDGE_PUBLIC_FORM_ID;
        break;
    }

    return snResources;
  };

  const solutionOrgs: SolutionOrgs = {
    [JOIST_DOMAIN]: {
      solutionOrgName: JoistSolutionOrgName,
      envSolutionOrgFormId: process.env.REACT_APP_JOIST_EDGE_FORM_ID,
      numberOfOnboardingForms: 4, // 2,
      envSolutionOrgTitle: process.env.REACT_APP_JOIST_SITE_TITLE,
      envSolutionOrgId: process.env.REACT_APP_SOLUTION_ID,
      IsInAppExperience: true,
      programCode: JoistCode,
      isNativeAppExperience,
      isNativeAppLoginExperience,
      solutionOrgHasOnlyHomeDepot: true,
      useHomeDepotOnlyBannerText: true,
      solutionOrgSpecificAppName: 'Joist Edge',
      enrollOnSplashScreen: false,
      paypalConnectionReturnUrl: `${process.env.REACT_APP_JOIST_BASE_URL}${WALLET_ROUTE}`,
      domainName: appDomain,
      productQuestion: false,
      baseUrl: process.env.REACT_APP_JOIST_BASE_URL,
    },
    [EVERPRO_DOMAIN]: getEverproResources(),
    [SERVICE_NATION_BY_EVER_PRO_DOMAIN]: getSNByEverproResources(),
    [UNKNOWN_DOMAIN]: {
      main: null,
    }, // for backward compatibility with web.dev.optimusapp.io and web.staging.optimusapp.io
  };

  return solutionOrgs[appDomain];
}
